import React, { useState } from "react";
import PropTypes from "prop-types";
import { CloseOutlined } from "@mui/icons-material";
import { Chip } from "@mui/material";

import "./ChipArrayInput.css";

/**
 * ChipArrayInput — a chip/tag style multi-value text input.
 * Press Enter or comma to add a value; click the × on a chip to remove it.
 * Backspace on empty input removes the last chip.
 */
const ChipArrayInput = ({
    label,
    value,
    onChange,
    error = false,
    helperText = null,
    placeholder = "Type and press Enter…",
    required = false,
}) => {
    const [inputVal, setInputVal] = useState("");
    const inputId = `chip-input-${label.toLowerCase().replace(/\s+/g, "-")}`;

    const addItem = () => {
        const trimmed = inputVal.trim().replace(/,$/, "");
        if (trimmed && !value.includes(trimmed)) {
            onChange([...value, trimmed]);
        }
        setInputVal("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addItem();
        } else if (e.key === "Backspace" && inputVal === "" && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    };

    const removeItem = (item) => onChange(value.filter((v) => v !== item));

    return (
        <div className={`chip-array-input${error ? " chip-array-input--error" : ""}`}>
            <p className="chip-array-input__label">
                {label}
                {required && <span className="chip-array-input__required"> *</span>}
            </p>

            <label className="chip-array-input__field" htmlFor={inputId}>
                {value.map((item) => (
                    <Chip
                        key={item}
                        label={item}
                        size="small"
                        onDelete={() => removeItem(item)}
                        deleteIcon={<CloseOutlined style={{ fontSize: 12 }} />}
                        sx={{
                            fontSize: "12px",
                            height: "24px",
                            background: "#f0fdf4",
                            color: "#166534",
                            border: "1px solid #bbf7d0",
                            "& .MuiChip-deleteIcon": { color: "#6b7280" },
                        }}
                    />
                ))}
                <input
                    id={inputId}
                    className="chip-array-input__text"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={addItem}
                    placeholder={value.length === 0 ? placeholder : ""}
                />
            </label>

            {error ? (
                <p className="chip-array-input__error">{helperText}</p>
            ) : (
                <p className="chip-array-input__hint">
                    Press <kbd>Enter</kbd> or <kbd>,</kbd> to add · <kbd>Backspace</kbd> to remove last
                </p>
            )}
        </div>
    );
};

ChipArrayInput.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
};



export default ChipArrayInput;
